import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, Clock, MapPin, Check } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { PHONES, EMAIL, HOURS, LOCATION } from "@/lib/site";

const LAND_OPTIONS = ["Yes", "No", "Looking to purchase"];
const LOOKING_OPTIONS = ["New home design", "Custom build", "Irregular block solution", "Design + build", "Other"];

const initial = { name: "", phone: "", email: "", owns_land: "Yes", block_location: "", looking_for: LOOKING_OPTIONS[0], message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const pill = (active) =>
    `px-4 py-2.5 rounded-full border text-[13px] font-medium transition-all duration-300 cursor-pointer ${
      active ? "bg-arc text-cream border-arc" : "bg-white border-line text-charcoal hover:border-arc/50"
    }`;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, phone, email and project details.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong");
      setDone(true);
      setForm(initial);
      toast.success("Enquiry received — we'll be in touch soon.");
    } catch (err) {
      toast.error(err.message || "Could not send your enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative bg-ink text-cream grain py-24 sm:py-32" data-testid="contact-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-[1fr_1.1fr] gap-14 relative z-10">
        <div>
          <Reveal>
            <p className="eyebrow text-sky mb-6">Start Your Project</p>
          </Reveal>
          <MaskedLines
            className="font-serif font-medium tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            lines={[<>Let's Build Something</>, <><span className="italic text-gradient">Extraordinary.</span></>]}
          />
          <Reveal delay={0.2}>
            <p className="mt-7 text-cream/75 leading-relaxed max-w-md">
              Have a block of land and a vision for your future home? Tell us about it.
            </p>
            <div className="mt-10 space-y-5">
              <div>
                <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50 mb-3">Call Us</p>
                <div className="space-y-2">
                  {PHONES.map((p) => (
                    <a
                      key={p.tel}
                      href={`tel:${p.tel}`}
                      data-testid={`contact-phone-${p.tel}`}
                      className="flex items-center gap-3 font-serif text-2xl sm:text-3xl text-cream hover:text-sky transition-colors duration-300"
                    >
                      <Phone size={19} className="text-sky" /> {p.display}
                    </a>
                  ))}
                </div>
              </div>
              <a
                href={`mailto:${EMAIL}`}
                data-testid="contact-email"
                className="flex items-center gap-3 text-cream/85 hover:text-sky transition-colors"
              >
                <Mail size={17} className="text-sky" /> {EMAIL}
              </a>
              <p className="flex items-center gap-3 text-cream/70 text-sm">
                <Clock size={16} className="text-sky" /> {HOURS.days} · {HOURS.time}
              </p>
              <p className="flex items-center gap-3 text-cream/70 text-sm">
                <MapPin size={16} className="text-sky" /> {LOCATION.line1} — {LOCATION.line2}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          {done ? (
            <div className="h-full min-h-[420px] bg-cream text-charcoal flex flex-col items-center justify-center text-center p-10" data-testid="enquiry-success-panel">
              <span className="w-14 h-14 rounded-full bg-arc text-cream flex items-center justify-center">
                <Check size={24} />
              </span>
              <h3 className="font-serif text-3xl mt-6">Enquiry received.</h3>
              <p className="mt-3 text-mist max-w-sm">
                Thank you — the ArcView Homes team will be in touch shortly. For anything urgent,
                call {PHONES[0].display}.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="bg-cream text-charcoal p-7 sm:p-10 space-y-6" data-testid="enquiry-form" noValidate>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="f-name" className="block text-xs font-semibold tracking-wide uppercase text-mist mb-2">Name *</label>
                  <input id="f-name" className="field" placeholder="Your full name" value={form.name} onChange={set("name")} data-testid="enquiry-input-name" required />
                </div>
                <div>
                  <label htmlFor="f-phone" className="block text-xs font-semibold tracking-wide uppercase text-mist mb-2">Phone *</label>
                  <input id="f-phone" type="tel" className="field" placeholder="Best number to reach you" value={form.phone} onChange={set("phone")} data-testid="enquiry-input-phone" required />
                </div>
              </div>
              <div>
                <label htmlFor="f-email" className="block text-xs font-semibold tracking-wide uppercase text-mist mb-2">Email *</label>
                <input id="f-email" type="email" className="field" placeholder="you@example.com" value={form.email} onChange={set("email")} data-testid="enquiry-input-email" required />
              </div>
              <div>
                <span className="block text-xs font-semibold tracking-wide uppercase text-mist mb-3">Do you own land?</span>
                <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="Do you own land">
                  {LAND_OPTIONS.map((o) => (
                    <button
                      type="button"
                      key={o}
                      role="radio"
                      aria-checked={form.owns_land === o}
                      onClick={() => setForm({ ...form, owns_land: o })}
                      data-testid={`enquiry-land-option-${o.toLowerCase().replace(/\s+/g, "-")}`}
                      className={pill(form.owns_land === o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="f-location" className="block text-xs font-semibold tracking-wide uppercase text-mist mb-2">Block location</label>
                <input id="f-location" className="field" placeholder="Suburb, e.g. Tarneit" value={form.block_location} onChange={set("block_location")} data-testid="enquiry-input-block-location" />
              </div>
              <div>
                <span className="block text-xs font-semibold tracking-wide uppercase text-mist mb-3">What are you looking for?</span>
                <div className="flex flex-wrap gap-2.5" role="radiogroup" aria-label="What are you looking for">
                  {LOOKING_OPTIONS.map((o) => (
                    <button
                      type="button"
                      key={o}
                      role="radio"
                      aria-checked={form.looking_for === o}
                      onClick={() => setForm({ ...form, looking_for: o })}
                      data-testid={`enquiry-looking-option-${o.toLowerCase().replace(/\s+/g, "-").replace(/\+/g, "plus")}`}
                      className={pill(form.looking_for === o)}
                    >
                      {o}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label htmlFor="f-message" className="block text-xs font-semibold tracking-wide uppercase text-mist mb-2">Tell us about your project *</label>
                <textarea id="f-message" rows={5} className="field resize-none" placeholder="Your land, your vision, your timeline — anything that helps us understand your project." value={form.message} onChange={set("message")} data-testid="enquiry-input-message" required />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="enquiry-form-submit-button"
                className="w-full bg-arc text-cream font-semibold text-[15px] py-4 rounded-full hover:bg-arc-dark transition-all duration-300 disabled:opacity-60"
              >
                {sending ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
}
